import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Divider, Form, message, Row, Typography } from 'antd';
import {
  LocationType,
  useGetMicrochipPriceQuery,
  useGetPublicLocationsQuery,
  useSignUpWithDonorsMutation,
} from 'gql/types';
import { Spin, Button, ControlledCheckbox } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { convertPoundsToKilograms } from 'modules/donor/utils/convertWeight';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { userFields } from './fields/userFields';
import FormSection from './FormSection';
import { PetAccordion } from './PetAccordion';
import { registerPetSchema, RegisterPetSchemaType } from './schema';
import { PetSchemaType } from './schema/petSchema';
import styles from './styles.module.scss';

const { Paragraph } = Typography;

const RegisterPetForm: FC = () => {
  const { t } = useTranslation('donor.RegisterPetForm');
  const [activePetIndex, setActivePetIndex] = useState(0);

  const registerPetForm = useForm<RegisterPetSchemaType>({
    resolver: yupResolver(registerPetSchema),
    mode: 'onChange',
    defaultValues: {
      pets: [{}],
      user: {},
    },
  });

  const {
    formState: { isDirty, isValid },
    control,
    watch,
    setValue,
    getValues,
    trigger,
    setError,
    clearErrors,
    reset,
    handleSubmit,
  } = registerPetForm;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'pets',
  });

  const sharedLocation = watch('pets.0.location');
  const sharedRegion = watch('pets.0.region');

  const currentPetSpecies = watch(`pets.${activePetIndex}.species`);
  const currentPetWeight = watch(`pets.${activePetIndex}.weight`);

  useEffect(() => {
    setValue('pets.0.location', '');
  }, [sharedRegion]);

  useEffect(() => {
    fields.forEach((_, index) => {
      if (index > 0) {
        setValue(`pets.${index}.region` as any, sharedRegion, {
          shouldValidate: true,
        });
        setValue(`pets.${index}.location` as any, sharedLocation, {
          shouldValidate: true,
        });
      }
    });
  }, [sharedRegion, sharedLocation, fields.length]);

  const { data: locationsData } = useGetPublicLocationsQuery({
    variables: {
      input: {
        take: 100,
      },
    },
  });

  const isDaycareLocationSelected = useMemo(() => {
    if (!sharedLocation || !locationsData?.getPublicLocations.data) {
      return false;
    }

    return Boolean(
      locationsData.getPublicLocations.data.find(
        (l) =>
          l.name === sharedLocation && l.type === LocationType.ForDaycareOwners
      )
    );
  }, [sharedLocation, locationsData]);

  const validateAttendDaycareFieldsValid = (petIndex: number) => {
    const pet = getValues(`pets.${petIndex}`);

    if (
      !pet?.wontRegularlyAttendDaycare &&
      !pet?.attendDaycareDays?.length &&
      isDaycareLocationSelected
    ) {
      setError(
        `pets.${petIndex}.attendDaycareDays`,
        {
          message: t('attendDaycareDaysValidationError'),
        },
        { shouldFocus: true }
      );
      return true;
    }

    clearErrors(`pets.${petIndex}.attendDaycareDays`);
    return false;
  };

  useEffect(() => {
    fields.forEach((_, index) => {
      setValue(`pets.${index}.attendDaycareDays`, undefined);
      setValue(`pets.${index}.wontRegularlyAttendDaycare`, false);
    });
  }, [sharedLocation]);

  useEffect(() => {
    if (currentPetWeight) {
      trigger(`pets.${activePetIndex}.weight`);
    }
  }, [currentPetSpecies, currentPetWeight, activePetIndex]);

  const handleAddPet = () => {
    append({
      region: sharedRegion,
      location: sharedLocation,
    } as PetSchemaType);
    setActivePetIndex(fields.length);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleRemovePet = (index: number) => {
    remove(index);
    if (activePetIndex >= index && activePetIndex > 0) {
      setActivePetIndex(activePetIndex - 1);
    }
  };

  const { data: getMicrochipQueryData } = useGetMicrochipPriceQuery();
  const microchipPriceText = useMemo(() => {
    if (getMicrochipQueryData?.getMicrochipPrice) {
      const { price } = getMicrochipQueryData.getMicrochipPrice;
      return `$${price}`;
    }
    return '...';
  }, [getMicrochipQueryData]);

  const [signUpWithDonors, signUpWithDonorsMutation] =
    useSignUpWithDonorsMutation({
      onCompleted: () => {
        window.scrollTo(0, 0);
        message.success({
          content: t('registrationSuccess'),
          duration: 5,
        });
        reset({ pets: [{}], user: {} });
        setActivePetIndex(0);
      },
      onError: (error) => {
        message.error(error.message);
      },
    });

  const submitForm = async (data: RegisterPetSchemaType) => {
    const { pets, user } = data;

    const hasErrors = pets.some((_, index) =>
      validateAttendDaycareFieldsValid(index)
    );

    if (hasErrors) {
      return;
    }

    await signUpWithDonors({
      variables: {
        input: {
          user: {
            ...user,
            locationPreferences: sharedLocation,
          },
          donors: pets.map(
            ({ location, wontRegularlyAttendDaycare, region, ...pet }) => ({
              ...pet,
              location: sharedLocation,
              weight: convertPoundsToKilograms(pet.weight),
              canRegularlyAttendDaycare: !wontRegularlyAttendDaycare,
            })
          ),
        },
      },
    });
  };

  const submit = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = getValues();
    await submitForm(formData);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  return (
    <>
      <Row gutter={[0, 10]}>
        <Col span={24}>
          <FormProvider {...registerPetForm}>
            <Spin spinning={signUpWithDonorsMutation.loading}>
              <Form layout="vertical">
                <PetAccordion
                  fields={fields}
                  control={registerPetForm.control}
                  activePetIndex={activePetIndex}
                  onPetChange={setActivePetIndex}
                  onAddPet={handleAddPet}
                  onRemovePet={handleRemovePet}
                  sharedRegion={sharedRegion}
                  sharedLocation={sharedLocation}
                  microchipPriceText={microchipPriceText}
                  isDaycareLocationSelected={isDaycareLocationSelected}
                />

                <Divider />

                <FormSection fields={userFields} fieldNamePrefix="user" />

                <Col span={24}>
                  <Paragraph>{t('registrationAgreement')}</Paragraph>
                </Col>
                <Col span={24}>
                  <ControlledCheckbox
                    controlled={{
                      control: registerPetForm.control,
                      name: 'privacyPolicyAgreement',
                    }}
                  >
                    <Paragraph>
                      {t('agreeToNavbb')}{' '}
                      <a
                        href="https://www.navbb.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('privacyPolicy')}
                      </a>
                      {'. '}
                      {t('privacyPolicyAfter')}
                    </Paragraph>
                  </ControlledCheckbox>
                </Col>
                <Button
                  size="large"
                  type="primary"
                  className={styles.submitButton}
                  onClick={submit}
                  disabled={!registerPetForm.formState.isValid}
                >
                  {t('submit')}
                </Button>
              </Form>
            </Spin>
          </FormProvider>
        </Col>
      </Row>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={signUpWithDonorsMutation.loading}
      />
    </>
  );
};

export default RegisterPetForm;
